// -------------------------------------------------
// Load multiple keys from .env
// -------------------------------------------------
const KEYS = (process.env.REACT_APP_OPENROUTER_KEYS || "")
  .split(",")
  .map(k => k.trim())
  .filter(Boolean);

if (KEYS.length === 0) {
  throw new Error("No OpenRouter keys found in .env file");
}

// -------------------------------------------------
// Rotate through keys for load balancing
// -------------------------------------------------
let keyIndex = 0;
function nextKey() {
  const key = KEYS[keyIndex];
  keyIndex = (keyIndex + 1) % KEYS.length;
  return key;
}

// -------------------------------------------------
// Model Chain (fallback sequence)
// -------------------------------------------------
const MODEL_CHAIN = [
  "openai/gpt-oss-20b:free",
  "meta-llama/llama-4-maverick:free",
  "meta-llama/llama-3.3-8b-instruct:free",
  "mistralai/mistral-7b-instruct:free",
  "google/gemma-3-27b-it:free",
  "meta-llama/llama-3.3-70b-instruct:free",
];

// -------------------------------------------------
// Clean text – **only remove OpenRouter artefacts**
// -------------------------------------------------
function cleanText(raw) {
  if (!raw) return "";
  return raw
    .replace(/\[OUT\]|\[\/OUT\]/gi, "")
    .replace(/<think>[\s\S]*?<\/think>/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// -------------------------------------------------
// Mood Tones
// -------------------------------------------------
const moodTones = {
  Friendly: "Your tone is warm, supportive, and empathetic — like a friend.",
  Honest: "Your tone is clear, direct, and truthful.",
  Curious: "Your tone is inquisitive and reflective.",
  Growth: "Your tone is motivational and focused on improvement.",
  Neutral: "Your tone is calm, factual, and balanced.",
};

// -------------------------------------------------
// Query (non-streaming)
// -------------------------------------------------
export async function queryModel(prompt, chatHistory = [], mood = "Neutral", customSystemPrompt = null) {
  return await _callOpenRouter(prompt, chatHistory, mood, false, null, customSystemPrompt);
}

// -------------------------------------------------
// Stream (streaming)
// -------------------------------------------------
export async function streamModel(prompt, chatHistory = [], mood = "Neutral", onChunk = null, customSystemPrompt = null) {
  return await _callOpenRouter(prompt, chatHistory, mood, true, onChunk, customSystemPrompt);
}

// -------------------------------------------------
// Core Logic
// -------------------------------------------------
async function _callOpenRouter(prompt, chatHistory, mood, stream = false, onChunk = null, customSystemPrompt = null) {
  const moodTone = moodTones[mood] || moodTones.Neutral;

  const systemContent = customSystemPrompt
    ? customSystemPrompt
    : `You are **VitStra AI**, a non-distracting, emotionally aware guide built to help users grow, learn, and reflect.
${moodTone}
Keep replies concise, grounded, and meaningful.`;

  // FIXED: Correctly map chatHistory (handles both {from, text} and {role, content})
  const mappedHistory = chatHistory.slice(-6).map(m => {
    if (m.role) {
      // AIBrain format: {role: "user/assistant", content: "..."}
      return { role: m.role, content: String(m.content || "").slice(0, 1000) };
    } else {
      // UI format fallback: {from: "user/ai", text: "..."}
      return {
        role: m.from === "user" ? "user" : "assistant",
        content: String(m.text || "").slice(0, 1000),
      };
    }
  });

  const messages = [
    { role: "system", content: systemContent },
    ...mappedHistory,
    { role: "user", content: String(prompt || "").slice(0, 1000) },
  ];

  console.log(`🧠 Querying "${prompt.substring(0, 50)}..." | ${KEYS.length} keys available`);

  for (let i = 0; i < MODEL_CHAIN.length; i++) {
    const model = MODEL_CHAIN[i];
    const apiKey = nextKey();
    const modelId = model.replace(":free", "");

    try {
      const res = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiKey}`,
          "HTTP-Referer": window.location.origin,
          "X-Title": "VitStra",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: modelId,
          messages,
          stream,
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error(`${modelId} failed: ${res.status} - ${errText.substring(0, 100)}`);
        if (
          errText.includes("requires payment") ||
          errText.includes("not available") ||
          errText.includes("Rate limit exceeded")
        ) {
          console.warn(`Skipping ${modelId} (payment/limit)`);
          continue;
        }
        throw new Error(`HTTP ${res.status}`);
      }

      // ------------------- NON-STREAM -------------------
      if (!stream) {
        const data = await res.json();
        const raw = data?.choices?.[0]?.message?.content ?? "";
        const text = cleanText(raw);
        if (text) {
          console.log(`✅ SUCCESS (${modelId}): "${text.substring(0, 100)}..."`);
          return { model: modelId, text };
        }
        console.warn(`⚠️ ${modelId} returned empty after cleaning`);
        continue; // try next model
      }

      // ------------------- STREAM -------------------
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          if (line === "data: [DONE]") break;

          try {
            const json = JSON.parse(line.slice(6));
            const delta = json.choices?.[0]?.delta?.content;
            if (delta) {
              fullText += delta;
              if (typeof onChunk === "function") onChunk(delta);
            }
          } catch (_) {}
        }
      }

      const text = cleanText(fullText);
      console.log(`STREAM FINISHED (${modelId}) – raw ${fullText.length} chars → cleaned ${text.length} chars`);

      if (text) {
        console.log(`✅ STREAM SUCCESS (${modelId}): "${text.substring(0, 100)}..."`);
        return { model: modelId, text };
      }
      console.warn(`⚠️ ${modelId} stream empty after cleaning – trying next model`);
      // **empty after cleaning → try next model**
    } catch (err) {
      console.error(`🚨 Exception in ${modelId}: ${err.message}`);
    }
  }

  // -------------------------------------------------
  // ALL MODELS FAILED OR RETURNED EMPTY
  // -------------------------------------------------
  console.error("❌ All models failed or returned empty text");
  throw new Error("All models failed or are rate-limited.");
}